import {
    ref, computed, type Ref,
} from 'vue';
import { io, type Socket } from 'socket.io-client';
import type { WebSocketConfigType } from '@/types/WebSocketType';
import type { UseWebSocketReturnType } from '@/types/composables/WebSocketComposableTypes';
import { ConnectionStatusEnum } from '@/types/enums/ConnectionStatusEnum';

export function useWebSocket(initialConfig: WebSocketConfigType): UseWebSocketReturnType {
    const socket: Ref<Socket | null> = ref(null);
    const connectionStatus = ref<ConnectionStatusEnum>(ConnectionStatusEnum.Disconnected);
    const connectionId = ref<string | null>(null);
    const connectionError = ref<string | null>(null);
    const reconnectAttempts = ref(0);
    const config = ref<WebSocketConfigType>(initialConfig);

    const maxReconnectAttempts = 5;
    const reconnectDelay = 1000;

    const isConnected = computed(() => connectionStatus.value === ConnectionStatusEnum.Connected);
    const isConnecting = computed(() => connectionStatus.value === ConnectionStatusEnum.Connecting);
    const isDisconnected = computed(() => connectionStatus.value === ConnectionStatusEnum.Disconnected);

    const connect = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (isConnecting.value || isConnected.value) {
                resolve();

                return;
            }

            connectionStatus.value = ConnectionStatusEnum.Connecting;
            connectionError.value = null;

            try {
                socket.value = io(`${config.value.url}`, {
                    reconnection: true,
                    reconnectionAttempts: maxReconnectAttempts,
                    reconnectionDelay: reconnectDelay,
                    extraHeaders: { token: config.value.token || '' },
                });

                socket.value.on('connect', () => {
                    connectionStatus.value = ConnectionStatusEnum.Connected;
                    connectionId.value = socket.value?.id || null;
                    reconnectAttempts.value = 0;
                    resolve();
                });

                socket.value.on('disconnect', () => {
                    connectionStatus.value = ConnectionStatusEnum.Disconnected;
                    connectionId.value = null;
                });

                socket.value.on('connect_error', error => {
                    console.error('WebSocket connection error:', error);
                    reconnectAttempts.value++;
                    connectionError.value = error instanceof Error ? error.message : 'Connection failed';

                    if (reconnectAttempts.value >= maxReconnectAttempts) {
                        connectionStatus.value = ConnectionStatusEnum.Disconnected;
                        reject(error);
                    }
                });
            } catch (error) {
                connectionStatus.value = ConnectionStatusEnum.Disconnected;
                connectionError.value = error instanceof Error ? error.message : 'Connection failed';
                reject(error);
            }
        });
    };

    const disconnect = (): void => {
        if (socket.value) {
            socket.value.disconnect();
            socket.value = null;
        }
        connectionStatus.value = ConnectionStatusEnum.Disconnected;
        connectionId.value = null;
        connectionError.value = null;
    };

    const on = (event: string, callback: (...args: any[]) => void): void => {
        socket.value?.on(event, callback);
    };

    const off = (event: string, callback?: (...args: any[]) => void): void => {
        if (callback) {
            socket.value?.off(event, callback);
        } else {
            socket.value?.off(event);
        }
    };

    const emit = (event: string, data?: any): void => {
        socket.value?.emit(event, data);
    };

    const removeAllEventListeners = (): void => {
        socket.value?.removeAllListeners();
    };

    const getSocket = (): Socket | null => {
        return socket.value;
    };

    const reconnectWithNewConfig = async (newConfig: WebSocketConfigType): Promise<void> => {
        config.value = newConfig;
        disconnect();
        await connect();
    };

    return {
        connectionStatus: connectionStatus.value,
        connectionId: connectionId.value,
        connectionError: connectionError.value,
        reconnectAttempts: reconnectAttempts.value,
        isConnected,
        isConnecting,
        isDisconnected,
        connect,
        disconnect,
        reconnectWithNewConfig,
        on,
        off,
        emit,
        removeAllEventListeners,
        getSocket,
    };
}
